import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/** Storage-object hrefs are the previewable evidence files. */
const isStorageObjectHref = (href: string | undefined): href is string =>
  !!href && href.includes('/api/storage/object/');

export function MarkdownBody({ content, onEvidenceFileClick, imageUrl, policyLinkContext }: {
  content: string;
  onEvidenceFileClick?: (href: string) => void;
  imageUrl?: (src: string) => string;
  policyLinkContext?: { workspaceId?: string; auditCycleId?: string };
}) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 className="mt-6 mb-3 text-[22px] font-semibold tracking-tight text-foreground first:mt-0">
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="mt-6 mb-2 text-[18px] font-semibold tracking-tight text-foreground first:mt-0">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="mt-5 mb-2 text-[15px] font-semibold text-text-secondary first:mt-0">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="mt-4 mb-1 text-[13px] font-semibold uppercase tracking-wide text-text-body first:mt-0">
            {children}
          </h4>
        ),
        p: ({ children }) => <p className="my-3 text-text-body">{children}</p>,
        ul: ({ children }) => (
          <ul className="my-3 ml-5 list-disc space-y-1 text-text-body marker:text-text-placeholder">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="my-3 ml-5 list-decimal space-y-1 text-text-body marker:text-text-placeholder">
            {children}
          </ol>
        ),
        li: ({ children }) => <li className="leading-6">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote className="my-4 border-l-2 border-cobalt-strong bg-background px-4 py-2 text-text-body italic">
            {children}
          </blockquote>
        ),
        a: ({ href, children }) => {
          // Evidence-file link → open the in-app preview (swap the modal to
          // that file) instead of navigating to the raw storage URL.
          if (onEvidenceFileClick && isStorageObjectHref(href)) {
            return (
              <button
                type="button"
                onClick={() => onEvidenceFileClick(href)}
                className="text-left text-cobalt-strong underline underline-offset-2 hover:text-cobalt-strong/80"
              >
                {children}
              </button>
            );
          }
          // Keep policy links in the current auditor cycle; the stored Markdown
          // remains portable and retains the canonical company policy URL.
          const path = href?.split('?')[0].split('#')[0];
          const segments = path?.split('/');
          const { workspaceId, auditCycleId } = policyLinkContext ?? {};
          const policyHref = workspaceId && auditCycleId && segments?.length === 5
            && segments[0] === '' && segments[1] === 'workspaces'
            && segments[2] === encodeURIComponent(workspaceId)
            && segments[3] === 'policies' && segments[4]
            ? `/workspaces/${encodeURIComponent(workspaceId)}/auditor-center/${encodeURIComponent(auditCycleId)}/policies/${segments[4]}${href!.slice(path!.length)}`
            : href;
          return (
            <a
              href={policyHref}
              target="_blank"
              rel="noreferrer"
              className="text-cobalt-strong underline underline-offset-2 hover:text-cobalt-strong/80"
            >
              {children}
            </a>
          );
        },
        code: ({ className, children }) => {
          const isBlock = (className ?? '').includes('language-');
          if (isBlock) {
            return (
              <code className={`${className ?? ''} font-mono text-[12px]`}>{children}</code>
            );
          }
          return (
            <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[12px] text-text-secondary">
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre className="my-4 overflow-x-auto rounded-md border border-border bg-surface-subtle p-4 text-[12px] leading-5">
            {children}
          </pre>
        ),
        table: ({ children }) => (
          <div className="my-4 overflow-x-auto">
            <table className="w-max min-w-full border-collapse border border-border text-[13px]">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-border bg-surface-subtle px-3 py-2 text-left text-[12px] font-semibold text-text-secondary">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-3 py-2 align-top text-text-body">
            {children}
          </td>
        ),
        hr: () => <hr className="my-6 border-border" />,
        img: ({ src, alt }) => (
          <img src={src && imageUrl ? imageUrl(src) : src} alt={alt ?? ''} className="my-4 max-w-full rounded-sm border border-border" />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
